import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import DataTable from "../../components/DataTable"; 
import { api, url } from "../../axios/api";
import { Calendar, DollarSign, Check, RefreshCw, FileText } from "lucide-react";

const RelatorioVendasGerais = () => {
    const user = useSelector((state) => state.user.user);
    const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");
    const empresaId = user?.empresa_id;

    const hojeStr = new Date().toISOString().split('T')[0];
    const [dataInicio, setDataInicio] = useState(hojeStr);
    const [dataFim, setDataFim] = useState(hojeStr);
    
    const [vendas, setVendas] = useState([]);
    const [totais, setTotais] = useState({ 
        totalVendasBruto: 0, 
        totalVendasLiquido: 0,
        totalIvaRetido: 0,
        totalRetencaoFonte: 0
    });
    const [loading, setLoading] = useState(false);

    const texts = {
        "pt-AO": {
            reportTitle: "Relatório de Vendas Gerais",
            reportSubtitle: "Histórico analítico de faturação e impostos",
            lblStart: "Data Inicial",
            lblEnd: "Data Final",
            btnFilter: "Filtrar",
            btnPdf: "Imprimir Relatório",
            cardGross: "Faturamento Bruto",
            cardNet: "Total Líquido",
            colDoc: "DOCUMENTO",
            colCliente: "CLIENTE",
            colBruto: "VALOR BRUTO",
            colIva: "IVA",
            colRetencao: "RET. FONTE",
            colLiquido: "LÍQUIDO",
            colData: "EMISSÃO"
        }
    };

    const t = texts[language] || texts["pt-AO"];

    const fetchVendasPeriodo = async () => {
        if (!empresaId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/app/relatorios/periodo/${empresaId}`, {
                params: { data_inicio: dataInicio, data_fim: dataFim }
            });
            setVendas(data.dados?.vendas || []);
            setTotais(data.totais || {});
        } catch (error) {
            console.error("Erro ao carregar relatório:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (empresaId) fetchVendasPeriodo();
    }, [empresaId]);

    const formatCurrency = (val) => new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA" }).format(val || 0);
    
    // URL para impressão via backend
    const printUrl = `${url}/index.php/api/prints/relatorio-vendas-gerais?empresa_id=${empresaId}&data_inicio=${dataInicio}&data_fim=${dataFim}`;

    return (
        <div id="layout-wrapper">
            <Navbar />
            <Sidebar />
            <main className="app-wrapper">
                <div className="win10-layout">
                    <div className="win10-card-main">
                        <div className="d-flex justify-content-between mb-4 align-items-end">
                            <div>
                                <h2>{t.reportTitle}</h2>
                                <p className="text-muted small">{t.reportSubtitle}</p>
                            </div>
                            <div className="d-flex gap-2">
                                <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="win10-input" />
                                <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="win10-input" />
                                
                                <button className="win10-btn-filter" onClick={fetchVendasPeriodo}>
                                    {loading ? <RefreshCw className="animate-spin" size={14} /> : <Calendar size={14} />} {t.btnFilter}
                                </button>

                                <a href={printUrl} target="_blank" rel="noopener noreferrer" className="win10-btn-pdf">
                                    <FileText size={14} /> {t.btnPdf}
                                </a>
                            </div>
                        </div>

                        <div className="kpi-container">
                            <div className="kpi-box">
                                <div className="kpi-title">{t.cardGross}</div>
                                <div className="kpi-value">{formatCurrency(totais.totalVendasBruto)}</div>
                            </div>
                            <div className="kpi-box kpi-liquido">
                                <div className="kpi-title">{t.cardNet}</div>
                                <div className="kpi-value">{formatCurrency(totais.totalVendasLiquido)}</div>
                            </div>
                        </div>

                        <DataTable 
                            columns={[
                                { title: t.colDoc, data: "numero" },
                                { title: t.colCliente, data: "Cliente.nome" },
                                { title: t.colBruto, data: "total_geral", render: (v) => formatCurrency(v) },
                                { title: t.colIva, data: "total_iva", render: (v) => formatCurrency(v) },
                                { title: t.colRetencao, data: "retencao_fonte", render: (v) => formatCurrency(v) },
                                { title: t.colLiquido, data: "liquido_real", render: (v) => formatCurrency(v) },
                                { title: t.colData, data: "data_emissao" }
                            ]}
                            data={vendas}
                        />
                    </div>
                </div>
            </main>
            <Footer />
            <style>{`
                .win10-layout { padding: 20px; }
                .win10-card-main { background: #fff; padding: 20px; border: 1px solid #e1e1e1; }
                .kpi-container { display: flex; gap: 15px; margin-bottom: 20px; }
                .kpi-box { background: #f9f9f9; padding: 15px; flex: 1; border-left: 4px solid #0078d4; }
                .kpi-liquido { border-left-color: #107c41; }
                .kpi-value { font-size: 20px; font-weight: bold; }
                .win10-input { padding: 5px; border: 1px solid #ccc; }
                .win10-btn-filter { background: #333; color: #fff; border: none; padding: 5px 15px; cursor: pointer; }
                .win10-btn-pdf { background: #a80000; color: #fff; border: none; padding: 5px 15px; text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
            `}</style>
        </div>
    );
};

export default RelatorioVendasGerais;
